import { Link } from 'react-router-dom';

interface DomainPriorityCardProps {
  rank: number;
  domain: string;
  score: number;
  total: number;
  priority: 'critical' | 'needs-work' | 'good' | 'strong';
  practiceLink: string;
  subtopics: { name: string; correct: number; total: number }[];
  color: { gradient: string; bg: string; text: string; border: string; badge: string };
  icon: string;
  weeklyGoal: string;
  tips: string[];
}

const PRIORITY_CONFIG = {
  critical: {
    label: 'Critical Focus',
    icon: 'ri-alarm-warning-fill',
    badgeClass: 'bg-red-100 text-red-700 border border-red-200',
    barClass: 'bg-red-500',
  },
  'needs-work': {
    label: 'Needs Work',
    icon: 'ri-error-warning-fill',
    badgeClass: 'bg-amber-100 text-amber-700 border border-amber-200',
    barClass: 'bg-amber-500',
  },
  good: {
    label: 'On Track',
    icon: 'ri-checkbox-circle-fill',
    badgeClass: 'bg-teal-100 text-teal-700 border border-teal-200',
    barClass: 'bg-teal-500',
  },
  strong: {
    label: 'Strong',
    icon: 'ri-medal-fill',
    badgeClass: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
    barClass: 'bg-emerald-500',
  },
};

export default function DomainPriorityCard({
  rank,
  domain,
  score,
  total,
  priority,
  practiceLink,
  subtopics,
  color,
  icon,
  weeklyGoal,
  tips,
}: DomainPriorityCardProps) {
  const pct = Math.round((score / total) * 100);
  const cfg = PRIORITY_CONFIG[priority];

  return (
    <div className={`bg-white rounded-2xl shadow-lg border-2 ${color.border} overflow-hidden`}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${color.gradient} p-5 text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="font-black text-lg">#{rank}</span>
            </div>
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className={`${icon} text-xl`} />
            </div>
            <div>
              <h3 className="font-black text-lg leading-tight">{domain}</h3>
              <p className="text-white/80 text-xs mt-0.5">{score}/{total} correct</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-4xl font-black">{pct}%</div>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full bg-white/20`}>
              <i className={`${cfg.icon} mr-1`} />
              {cfg.label}
            </span>
          </div>
        </div>

        {/* Score bar */}
        <div className="mt-4 bg-white/20 rounded-full h-2.5 overflow-hidden">
          <div
            className="h-full bg-white rounded-full transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        {/* Subtopic breakdown */}
        <div className="mb-5">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Subtopic Breakdown</h4>
          <div className="space-y-2.5">
            {subtopics.map((st) => {
              const stPct = Math.round((st.correct / st.total) * 100);
              const isWeak = stPct < 60;
              return (
                <div key={st.name}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-semibold ${isWeak ? 'text-red-600' : 'text-gray-700'} flex items-center gap-1`}>
                      {isWeak && <i className="ri-alert-line text-red-400" />}
                      {st.name}
                    </span>
                    <span className={`text-xs font-bold ${isWeak ? 'text-red-600' : 'text-gray-500'}`}>
                      {st.correct}/{st.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${isWeak ? 'bg-red-400' : cfg.barClass}`}
                      style={{ width: `${stPct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Weekly goal */}
        <div className={`${color.bg} rounded-xl p-3 mb-4 border ${color.border}`}>
          <p className="text-xs font-bold text-gray-600 mb-1 flex items-center gap-1.5">
            <i className="ri-calendar-check-line" />
            Weekly Study Goal
          </p>
          <p className={`text-sm font-semibold ${color.text}`}>{weeklyGoal}</p>
        </div>

        {/* Tips */}
        <div className="mb-5">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Study Tips</h4>
          <ul className="space-y-1.5">
            {tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                <i className="ri-lightbulb-line text-amber-500 mt-0.5 flex-shrink-0" />
                {tip}
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <Link
          to={practiceLink}
          className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm bg-gradient-to-r ${color.gradient} text-white hover:opacity-90 transition-opacity whitespace-nowrap`}
        >
          <i className="ri-play-circle-line text-base" />
          Practice This Domain
        </Link>
      </div>
    </div>
  );
}
